import * as React from 'react';
import { Button, Form } from 'react-bootstrap';
import { getLocalization } from 'global/global';
import { useAppDispatch, useAppSelector } from 'index';
import { AdminUserDraft, SSO_FIELDS, SsoField } from 'Interfaces/Admin';
import { showAlert, showConfirm } from 'actions';
import { saveAdminUser } from 'actions/adminUserActions';
import { adminGroupsSelector, adminUsersSelector, findParentOrganization } from 'reducers/admin/selectors';
import { canActivateUser, canAddActiveUser, canAssignOfficeRole } from 'reducers/admin/seats';
import { collectGroupUsers } from 'reducers/admin/selectors';
import { showColumns } from 'reducers/admin/ui';
import { showInviteTemplate } from '../modals/InviteTemplateModal';
import LocationTree from './LocationTree';
import PasswordField from './PasswordField';
import SsoOptions from './SsoOptions';
import { isWeakPassword, prepareUserPayload, usesSso, validateUser } from './validation';

interface Props {
  /** null when adding a new user. */
  userId: number | null;
}

const ROLES = [
  { value: 'ordinary', label: 'ordinaryLabel' },
  { value: 'viewer', label: 'viewerLabel' },
  { value: 'taskAssignee', label: 'taskAssigneeLabel' },
  { value: 'enumerator', label: 'enumLabel' },
  { value: 'modeler', label: 'modelerLabel' },
  { value: 'admin', label: 'adminLabel' },
];

const NOTIFICATIONS: { field: keyof AdminUserDraft; label: string }[] = [
  { field: 'newDataNotification', label: 'newdatanotification' },
  { field: 'updatedDataNotification', label: 'editeddatanotification' },
  { field: 'updatedCreatedDataNotification', label: 'notifydataupdated' },
];

const EMPTY: AdminUserDraft = { userName: '', groupId: 0, roles: 'ordinary', active: true };

/** Replaces UserView.js. */
export default function UserForm(props: Props) {
  const dispatch = useAppDispatch();
  const groups = useAppSelector(adminGroupsSelector);
  const users = useAppSelector(adminUsersSelector);
  const hierarchies = useAppSelector((state) => state.admin.meta.hierarchies);
  const account = useAppSelector((state) => state.admin.account);

  const existing = props.userId !== null ? users.find((u) => u.id === props.userId) : undefined;

  const [draft, setDraft] = React.useState<AdminUserDraft>(existing || EMPTY);
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  /** Set once the admin has accepted a weak password, mirroring window.ignorePassword. */
  const [allowWeakPassword, setAllowWeakPassword] = React.useState(false);

  React.useEffect(() => {
    setDraft(existing || { ...EMPTY });
    setPassword('');
    setConfirmPassword('');
    setAllowWeakPassword(false);
  }, [props.userId]);

  const set = <K extends keyof AdminUserDraft>(field: K, value: AdminUserDraft[K]) =>
    setDraft((d) => ({ ...d, [field]: value }));

  const isNew = !existing;
  const isResponsible = existing?.responsibleUser === true;
  const sso = usesSso(draft);
  const group = groups.find((g) => g.id === draft.groupId);
  const hierarchy = group?.locationHierarchyId
    ? hierarchies.find((h) => h.id === group.locationHierarchyId)
    : undefined;

  /** The four SSO options are mutually exclusive. */
  const onSsoChange = (field: SsoField, checked: boolean) => {
    setDraft((d) => {
      const cleared = SSO_FIELDS.reduce((acc, f) => ({ ...acc, [f]: false }), {});
      return { ...d, ...cleared, [field]: checked };
    });
    if (checked) {
      setPassword('');
      setConfirmPassword('');
    }
  };

  const onGroupChange = (groupId: number) => {
    // An organization at its seat limit cannot take another user.
    if (isNew && groupId) {
      const target = groups.find((g) => g.id === groupId);
      const organization = target?.organization
        ? target
        : findParentOrganization(groups, groupId);
      if (organization?.orgMaxUsers && organization.orgMaxUsers > 0) {
        const current = collectGroupUsers(users, groups, organization.id, true, true).length;
        if (current >= organization.orgMaxUsers) {
          dispatch(showAlert({
            visible: true,
            message: `${getLocalization('maxusersreached')} ${target?.name || ''}`.trim(),
          }));
          return;
        }
      }
    }
    setDraft((d) => ({ ...d, groupId, locationIds: null, locationNames: null }));
  };

  const onRoleChange = (role: string) => {
    if (draft.active !== false) {
      const check = canAssignOfficeRole(account, role === 'admin' ? 'admin,modeler' : role);
      if (!check.allowed) {
        dispatch(showAlert({ visible: true, message: getLocalization('maxOfficeUsers') }));
        return;
      }
    }
    setDraft((d) => {
      // A new office user opts into data notifications; a field user does not.
      if (!isNew) {
        return { ...d, roles: role };
      }
      const office = role === 'admin' || role === 'modeler';
      return {
        ...d,
        roles: role,
        newDataNotification: office,
        updatedDataNotification: office,
        updatedCreatedDataNotification: office ? d.updatedCreatedDataNotification : false,
      };
    });
  };

  const onActiveChange = (checked: boolean) => {
    if (existing && existing.id === account.userId && !checked) {
      dispatch(showAlert({ visible: true, message: getLocalization('userActivateError') }));
      return;
    }
    if (existing && checked && !existing.active) {
      const check = canActivateUser(account, draft.roles || '');
      if (!check.allowed) {
        dispatch(showAlert({
          visible: true,
          message: getLocalization(check.denied === 'office' ? 'maxOfficeUsers' : 'maxActiveUsers'),
        }));
        return;
      }
    }
    // An inactive user is never sent an invitation.
    setDraft((d) => ({
      ...d, active: checked, sendInvitationEmail: checked ? d.sendInvitationEmail : false,
    }));
  };

  const commit = (payload: AdminUserDraft) => {
    void dispatch(saveAdminUser(payload)).then((saved) => {
      if (saved) {
        dispatch(showColumns({ path: [{ type: 'group', id: payload.groupId }] }));
      }
    });
  };

  const save = () => {
    const failure = validateUser(draft, password, confirmPassword, {
      adminDisablePassword: account.adminDisablePassword,
      isExisting: !isNew,
    });
    if (failure) {
      dispatch(showAlert({ visible: true, message: getLocalization(failure.message) }));
      return;
    }

    if (isNew && draft.active !== false) {
      const check = canAddActiveUser(account, draft.roles === 'admin' ? 'admin,modeler' : draft.roles || '');
      if (!check.allowed) {
        dispatch(showAlert({
          visible: true,
          message: account.responsible_user
            ? getLocalization('maxUsersContactPerson')
            : getLocalization('maxUsers').replace(/{{contactPerson}}/g, account.contactEmail || ''),
        }));
        return;
      }
    }

    const payload = prepareUserPayload(draft, password, !isNew);

    // A long but simple password is allowed once the admin confirms.
    const passwordSet = !sso && !draft.sendInvitationEmail
      && !account.adminDisablePassword && (isNew || password);
    if (passwordSet && !allowWeakPassword && isWeakPassword(password)) {
      dispatch(showConfirm({
        visible: true,
        message: getLocalization('strongPassword'),
        onConfirm: () => {
          setAllowWeakPassword(true);
          commit(payload);
        },
      }));
      return;
    }

    commit(payload);
  };

  return (
    <div className="admin-form p-3">
      <Form onSubmit={(e) => e.preventDefault()}>
        <Form.Group className="mb-2">
          <Form.Label>{getLocalization('userlevel')}</Form.Label>
          {ROLES.map(({ value, label }) => (
            <Form.Check
              key={value}
              type="radio"
              id={`user-role-${value}`}
              name="role"
              label={(<span dangerouslySetInnerHTML={{ __html: getLocalization(label)}} />)}
              checked={draft.roles === value || (value === 'admin' && draft.roles === 'admin,modeler')}
              onChange={() => onRoleChange(value)}
            />
          ))}
        </Form.Group>

        <SsoOptions
          user={draft}
          samlAvailable={account.samlEnabled && account.samlIdpMetadata}
          onChange={onSsoChange}
        />

        {!sso && (
          <Form.Group className="mb-2">
            <Form.Label>{getLocalization('fullname')}</Form.Label>
            <Form.Control
              size="sm"
              value={draft.fullName || ''}
              onChange={(e) => set('fullName', e.target.value)}
            />
          </Form.Group>
        )}

        <Form.Group className="mb-2">
          <Form.Label>{getLocalization('selectgroup')} <span className="text-danger">*</span></Form.Label>
          <Form.Control
            as="select"
            size="sm"
            id="user-group"
            value={draft.groupId || ''}
            disabled={isResponsible}
            onChange={(e) => onGroupChange(Number(e.target.value))}
          >
            <option value="" />
            {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </Form.Control>
        </Form.Group>

        {hierarchy && (
          <Form.Group className="mb-2">
            <Form.Label>{getLocalization('userlochierarchy')}</Form.Label>
            <LocationTree
              hierarchy={hierarchy}
              selectedKeys={(draft.locationIds || []).map(String)}
              onChange={(keys, titles) => setDraft((d) => ({
                ...d, locationIds: keys.map(Number), locationNames: titles,
              }))}
            />
          </Form.Group>
        )}

        <Form.Group className="mb-2">
          <Form.Label>{getLocalization('email')}</Form.Label>
          <Form.Control
            id="user-email"
            size="sm"
            value={draft.email || ''}
            onChange={(e) => set('email', e.target.value)}
          />
          <Form.Text>{getLocalization('emailInfo')}</Form.Text>
        </Form.Group>

        {!sso && (
          <>
            <Form.Group className="mb-2">
              <Form.Label>{getLocalization('usernameNoEmail')}</Form.Label>
              <Form.Control
                id="user-username"
                size="sm"
                value={draft.userName || ''}
                onChange={(e) => set('userName', e.target.value.trim())}
              />
            </Form.Group>

            <hr />
            {!password && !confirmPassword && (
              <p className="text-info">{getLocalization('inviteAlert')}</p>
            )}
            <PasswordField
              id="user-password"
              label="password"
              value={password}
              onChange={setPassword}
              visible={showPassword}
              onToggle={() => setShowPassword(!showPassword)}
            />
            <PasswordField
              id="user-confirm-password"
              label="confirmpassword"
              value={confirmPassword}
              onChange={setConfirmPassword}
              visible={showConfirmPassword}
              onToggle={() => setShowConfirmPassword(!showConfirmPassword)}
            />
            <Form.Text>{getLocalization('passwordAdvice')}</Form.Text>
            <hr />
          </>
        )}

        <Form.Group className="mb-2">
          <Form.Label>{getLocalization('phone')}</Form.Label>
          <Form.Control
            id="user-phone"
            size="sm"
            value={draft.phoneNumber || ''}
            onChange={(e) => set('phoneNumber', e.target.value)}
          />
        </Form.Group>

        {account.accessSharedDataOnlyEnabled && (
          <Form.Check
            id="user-access-shared-data-only"
            label={getLocalization('accessSharedDataOnly')}
            checked={!!draft.accessSharedDataOnly}
            onChange={(e) => set('accessSharedDataOnly', e.target.checked)}
          />
        )}

        <Form.Check
          id="user-active"
          label={getLocalization('active')}
          checked={draft.active !== false}
          disabled={isResponsible}
          onChange={(e) => onActiveChange(e.target.checked)}
        />

        {/* An invitation is pointless once the user has logged in at least once. */}
        {!existing?.lastLogin && (
          <Form.Group className="mb-2">
            <Form.Check
              id="user-send-invitation"
              label={getLocalization('sendinvitationemail')}
              checked={!!draft.sendInvitationEmail}
              disabled={draft.active === false}
              onChange={(e) => set('sendInvitationEmail', e.target.checked)}
            />
            <Button size="sm" onClick={() => showInviteTemplate(sso)}>
              {getLocalization('inviteTemplate')}
            </Button>
          </Form.Group>
        )}

        <Form.Group className="mb-2">
          <Form.Label>{getLocalization('emailTo')}</Form.Label>
          <Form.Control
            as="textarea"
            value={draft.sendReportTo || ''}
            onChange={(e) => set('sendReportTo', e.target.value)}
          />
        </Form.Group>

        <div className="panel-default">
          <div className="panel-heading"><strong>{getLocalization('emailNotification')}</strong></div>
          <div className="panel-body">
            {NOTIFICATIONS.map(({ field, label }) => (
              <Form.Check
                key={String(field)}
                id={`user-${String(field)}`}
                label={getLocalization(label)}
                checked={!!draft[field]}
                onChange={(e) => set(field, e.target.checked as never)}
              />
            ))}
          </div>
        </div>

        <Form.Group className="mb-2">
          <Form.Check
            id="user-two-factor"
            label={getLocalization('hasTwoFactorAuth')}
            checked={!!draft.twoFactorAuth}
            // Two factor auth can be revoked here but only enrolled by the user.
            disabled={!existing?.twoFactorAuth}
            onChange={(e) => set('twoFactorAuth', e.target.checked ? existing?.twoFactorAuth || null : null)}
          />
          <Form.Text>{getLocalization('hasTwoFactorAuthInfo')}</Form.Text>
        </Form.Group>
      </Form>

      <div className="form-actions">
        <Button size="sm" onClick={save}>
          {getLocalization(existing ? 'update' : 'save')}
        </Button>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() => dispatch(showConfirm({
            visible: true,
            message: getLocalization(existing ? 'confirmCancelEditUser' : 'confirmCancelNewUser'),
            onConfirm: () => { dispatch(showColumns({ path: [] })); },
          }))}
        >
          {getLocalization('cancel')}
        </Button>
      </div>
    </div>
  );
}
